import React, { FC, useCallback, useEffect } from "react";
import clsx from "clsx";
import { useOpenModalFromCard } from "providers/content-modal-manager";

import Tile from "components/tile";

import { Button } from "../Button";
import { useTranslation } from "lib/i18n/use-translation";
import { useInViews } from "utility/useInViews";
import { trackEvent } from "utility/mixpanel";
import { logAnalyticsEvent } from "utility/FirebaseAnalytics";
import { getTrackingParamsFromUrl } from "../../../apps/user/learner/components/discover/utils";

import {
  LearnerSessionCardWebPartsFragment,
  LearnerGroupSessionAction,
} from "../../../apps/user/learner/api/types";

import styles from "./session-tile.module.scss";
import { Link } from "react-router-dom";

export interface SessionTilePropsType {
  session: LearnerSessionCardWebPartsFragment;
  listName?: string;
  listPosition?: number;
  actions: {
    [key in LearnerGroupSessionAction]?: {
      loading: boolean;
      action: (listName?: string, listPosition?: number) => void;
    };
  };
  showActions?: boolean;
}

const SessionTile: FC<SessionTilePropsType> = ({
  session,
  listName,
  listPosition,
  actions,
  showActions = true,
}) => {
  const { t } = useTranslation("components");
  const { __typename, id, imageUrl, title, subtitle, time, buttonState } =
    session;

  const { ref, inView } = useInViews();

  const userID = localStorage.getItem("userID");
  const companyName = localStorage.getItem("companyName") || "NA";

  const logImpression = useCallback(() => {
    const { appliedFilters, searchTerm } = getTrackingParamsFromUrl();

    if (__typename === "LearnerGroupSession") {
      trackEvent("Live Session Card Impression", {
        "Session ID": id,
        "Session Title": title,
        "List Name": listName,
        "List Position": listPosition,
        "Current Page": window.location.href,
        "Applied Filters": appliedFilters,
        "Search Term": searchTerm,
      });
      logAnalyticsEvent("group_session_impression", {
        user_id: userID,
        user_id_wone: userID,
        user_company_name: companyName,
        group_session_instance_id: id,
        group_session_name: title,
        list_name: listName,
        list_position: listPosition,
        page_title: document.title,
        page_path: window.location.pathname,
      });
    } else if (__typename === "LearnerPrivateSession") {
      trackEvent("Private Session Card Impression", {
        "Session ID": id,
        "Session Title": title,
        "List Name": listName,
        "List Position": listPosition,
        "Current Page": window.location.href,
        "Applied Filters": appliedFilters,
        "Search Term": searchTerm,
      });
      logAnalyticsEvent("private_session_impression", {
        user_id: userID,
        user_id_wone: userID,
        user_company_name: companyName,
        private_session_instance_id: id,
        private_session_name: title,
        list_name: listName,
        list_position: listPosition,
        page_title: document.title,
        page_path: window.location.pathname,
      });
    }
  }, [__typename, id, listName, listPosition, title, userID, companyName]);

  const logClick = useCallback(() => {
    const { appliedFilters, searchTerm } = getTrackingParamsFromUrl();

    if (__typename === "LearnerGroupSession") {
      trackEvent("Live Session Card Clicked", {
        "Session ID": id,
        "Session Title": title,
        "List Name": listName,
        "List Position": listPosition,
        "Session Pillar": session.pillarLabel,
        "Current Page": window.location.href,
        "Applied Filters": appliedFilters,
        "Search Term": searchTerm,
      });
      logAnalyticsEvent("group_session_click", {
        user_id: userID,
        user_id_wone: userID,
        user_company_name: companyName,
        group_session_instance_id: id,
        group_session_name: title,
        list_name: listName,
        list_position: listPosition,
        page_title: document.title,
        page_path: window.location.pathname,
      });
    } else if (__typename === "LearnerPrivateSession") {
      trackEvent("Private Session Card Clicked", {
        "Session ID": id,
        "Session Title": title,
        "List Name": listName,
        "List Position": listPosition,
        "Session Pillar": session.pillarLabel,
        "Current Page": window.location.href,
        "Applied Filters": appliedFilters,
        "Search Term": searchTerm,
      });
      logAnalyticsEvent("private_session_click", {
        user_id: userID,
        user_id_wone: userID,
        user_company_name: companyName,
        private_session_instance_id: id,
        private_session_name: title,
        list_name: listName,
        list_position: listPosition,
        page_title: document.title,
        page_path: window.location.pathname,
      });
    }
  }, [
    __typename,
    id,
    listName,
    listPosition,
    title,
    userID,
    companyName,
    session.pillarLabel,
  ]);

  useEffect(() => {
    if (inView) {
      logImpression();
    }
  }, [inView, logImpression]);

  const handleClickActionButton = (
    event: React.MouseEvent,
    action: LearnerGroupSessionAction
  ) => {
    event.preventDefault();
    event.stopPropagation();

    actions[action]?.action(listName, listPosition);
  };

  const openModalFromCard = useOpenModalFromCard();

  const handleClick = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    openModalFromCard({ type: "session", id: id });
    logClick();
  };

  return (
    <Tile className={"no-shadow"}>
      <Link
        ref={ref}
        className={styles["child-tile"]}
        to="#"
        onClick={handleClick}
        state={{ listName, listPosition, pageTitlePrefix: title }}
      >
        <img
          className={clsx(
            styles["picture"],
            !showActions && styles["picture--fixed"]
          )}
          src={imageUrl}
          alt="session-background"
        />
        <div className={styles["session-detail"]}>
          <p className={styles["time"]}>{time}</p>
          <h3 className={styles["title"]}>{title}</h3>
          <p className={styles["sub-title"]}>{subtitle}</p>
        </div>
        {showActions && buttonState.isSessionOngoing && (
          <div className={styles["button-container"]}>
            <Button
              loading={actions["JOIN"]?.loading}
              onClick={(event) =>
                handleClickActionButton(event, LearnerGroupSessionAction.Join)
              }
              variant="enclosed-primary"
              disabled={buttonState.isSessionCompleted}
            >
              {t("components:sessionTile.join")}
            </Button>
          </div>
        )}
        {showActions && !buttonState.isSessionOngoing && (
          <div className={styles["button-container"]}>
            <Button
              loading={actions["REGISTER"]?.loading}
              onClick={(event) =>
                handleClickActionButton(
                  event,
                  LearnerGroupSessionAction.Register
                )
              }
              variant="enclosed-secondary"
              monochromeColour="black"
              disabled={buttonState.isRegistered}
            >
              {t("components:sessionTile.reserve")}
            </Button>
          </div>
        )}
      </Link>
    </Tile>
  );
};
export default SessionTile;
